import { MSCalendar } from "../..";
import { getEditTemplate } from "./week.templates";
import styles from "../styles.css";
import { IEvent, IWeeklyState } from "@lib/store/src/calendar";
import { UiFloatPopover } from "../../../common/popover";
import { WeekModal } from "./week";
import { popupEvent } from "../modal.templates";
import i18next from "i18next";

/**
 * Редактирование недельного расписания (расширение WeekModal class)
 * @class EditWeekModal
 * @extends WeekModal
 * @classdesc EditWeekModal
 * */
export class EditWeekModal extends WeekModal {
  selectedDay: number = null;

  constructor(rootID: string, root: MSCalendar) {
    super(rootID, root);
    super.init(getEditTemplate(), styles);

    this.validateForm = () => {
      this.isValidForm([this.selectedDay]);
    }
  }

  /**
   * Сброс формы до начальных значений
   */
  resetForm() {
    super.resetForm();

    // Обновление
    this.renderEvents();
  }

  /**
   * Инициализация обработчиков событий
   */
  onStartup() {
    const modal = this.popup.modalComponent;

    modal.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Обработка кнопки "Сохранить"
      if (target.closest("#submit")) {
        this.save();
      }

      // Обработка кнопки "Удалить"
      if (target.closest("#delete")) {
        this.delete(e as MouseEvent);
      }

      // Обработка кнопки "Сбросить"
      if (target.closest("#reset")) {
        this.close();
      }

      // Обработка кнопки "Добавить событие"
      if (target.closest("#add-event")) {
        this.addEventTime();
      }
    });

    // Обработка изменения дня недели
    modal.addEventListener("fwChange", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest("#week-day-edit")) {
        this.changeCurrentDay(e as CustomEvent);
      }
    });

    this.bodyEvents?.addEventListener("click", (e) => this.onClickEvent(e));
  }

  /**
   * Обработчик удаления расписания для выбранного дня
   * */
  delete(e: MouseEvent) {
    setTimeout(() => {
      const popover = new UiFloatPopover(
        popupEvent({
          title: i18next.t($ => $.scheduleBacnet.deletion),
          desc: i18next.t($ => $.scheduleBacnet.eventDeleteConfirmation),
        }),
        {
          x: e.clientX,
          y: e.clientY,
        }
      );

      popover.root.querySelector("#primary")?.addEventListener("click", () => {
        this.updateWeeklySchedule(this.selectedDay, null);
        popover.destroy();
        this.close();
      });

      popover.root
        .querySelector("#secondary")
        ?.addEventListener("click", () => popover.destroy());
    }, 0);
  }

  /**
   * Обработчик кнопки сохранения нового расписания
   * */
  save() {
    if (!this.isValidForm([this.selectedDay])) return;
    
    this.updateWeeklySchedule(this.selectedDay, [...this.eventsState]);

    this.close();
  }

  /**
   * Обновление стейта
   * @param {number} day Индекс дня для редактирования стейта
   * @param {Array<IEvent>} events Список событий
   * */
  updateWeeklySchedule(day: number, events: Array<IEvent>) {
    const weeklySchedule: IWeeklyState = JSON.parse(
      JSON.stringify(this.root.store.weeklySchedule)
    );

    weeklySchedule[day] = events;

    this.store.onChange("weeklySchedule", weeklySchedule);

    this.root.saveSchedule();
  }

  /**
   * Установка выбранного дня в radio-кнопке по умолчанию
   */
  initCurrentWeekDay() {
    const radioButtons = this.popup.modalComponent.querySelectorAll(
      "#week-day-edit fw-radio"
    );
    const currentDay = this.root.store.date.day();
    this.selectedDay = (currentDay + 6) % 7; // Сдвигаем индекс, чтобы 0 был понедельником

    Array.from(radioButtons).forEach((el, i) => {
      el.checked = i === this.selectedDay;
    });
  }

  /**
   * Обработчик изменения дня недели по радио-кнопке
   * @param {CustomEvent} e CustomEvent
   */
  changeCurrentDay(e: CustomEvent) {
    if (this.selectedDay === Number(e?.detail?.value)) return;

    this.selectedDay = Number(e?.detail?.value) || 0;
    this.resetForm(); // Rerender формы для подгрузки событий
  }

  /**
   * Отображение существующих событий по выбранному дню недели
   */
  renderEvents() {
    const weeklySchedule: IWeeklyState = JSON.parse(
      JSON.stringify(this.root.store.weeklySchedule)
    );

    if (weeklySchedule[this.selectedDay]) {
      weeklySchedule[this.selectedDay].forEach((event) => {
        setTimeout(() => {
          this.addEventTime(event);
        }, 0);
      });
    }
  }

  /**
   * Открытие модального окна
   */
  open() {
    this.popup.modalComponent.open();

    this.initCurrentWeekDay(); // Выбор дня по умолчанию из календаря
    this.resetForm(); // Сброс формы
    this.popup.uiModal.resetSize();

    this.validateForm();
  }
}
